import React, { useEffect, useMemo, useRef, useState } from 'react';
import usePortfolioDesign from './usePortfolioDesign';
import { searchPortfolioSelection } from '../utils/portfolioDesign';
import { indexConstraintEvaluation } from '../components/optimizer/optimizerRuleRegistry';
import RuleLimitInput from '../components/optimizer/RuleLimitInput';
import Select from '../components/ui/Select';
import { createOptimizerOperationId } from '../utils/optimizerConstraintEditor';
import { attributeSearchUnavailableReason, autoUnavailableReason, prepareOptimizerSearch, searchableConstraints } from '../utils/optimizerSearchRanges';

export default function useOptimizerSearch({ bridge, enabled, objective, runSingle, currentPortfolioLoaded = false }) {
    const model = usePortfolioDesign(bridge, enabled);
    const selection = searchPortfolioSelection({ study: model.study, candidate: model.candidate,
        currentResult: bridge?.currentResult, currentRunOperationId: bridge?.currentRunOperationId,
        currentPortfolioLoaded, stage: model.stage });
    const [ranges, setRanges] = useState({});
    const [options, setOptions] = useState({ trial_budget: 64, search_seconds: 600, trial_seconds: 15 });
    const [error, setError] = useState(null);
    const commandIdentity = useRef(null);
    useEffect(() => { setRanges({}); setError(null); commandIdentity.current = null; }, [bridge?.scopeKey, bridge?.savedConfigurationVersion]);
    const index = useMemo(() => indexConstraintEvaluation(bridge?.baseline?.constraint_evaluation), [bridge?.baseline]);
    const draft = bridge?.initialDraft;
    const dimensions = draft ? searchableConstraints(draft, index).filter((record) => record.enabled && ranges[record.parameter]) : [];
    const isSearch = enabled && dimensions.length > 0;
    const active = ['queued', 'running', 'cancel_requested'].includes(model.study?.status);
    const modeEditor = ({ parameter, enabled: applied, onEnabledChange, label, disabled }) => {
        // Use current selected membership, even while a historical result is displayed.
        const unavailableReason = autoUnavailableReason(parameter) ?? (parameter?.startsWith('group:')
            ? attributeSearchUnavailableReason(index.byRuleId.get(parameter.slice('group:'.length))) : null);
        return (
        <Select value={!applied ? 'off' : ranges[parameter] ? 'auto' : 'fixed'}
            disabled={disabled} aria-label={`${label} mode`} className="w-20"
            onChange={(event) => {
                const mode = event.target.value;
                onEnabledChange(mode !== 'off');
                setError(null);
                if (mode === 'auto') {
                    setRanges((current) => ({ ...current, [parameter]: { auto: true } }));
                } else setRanges((current) => { const next = { ...current }; delete next[parameter]; return next; });
            }}>
            <option value="off">Off</option><option value="fixed">Fixed</option>
            <option value="auto" disabled={!parameter || Boolean(unavailableReason)}>
                {unavailableReason ? `Auto — ${unavailableReason}` : 'Auto'}
            </option>
        </Select>
        );
    };
    const rangeEditor = (props) => ranges[props.parameter]
        ? <span className="text-[0.75rem] leading-[1.66] text-content-secondary">Automatic</span>
        : <RuleLimitInput {...props} value={props.value == null ? null : props.value * (props.multiplier ?? 1)} ariaLabel={`${props.label} limit`} onChange={(v) => props.onChange(v == null ? null : v / (props.multiplier ?? 1))} width={props.unit === 'usd' ? 140 : 108} />;
    async function run() {
        setError(null);
        if (!enabled) return runSingle();
        try {
            if (Object.keys(ranges).length && (!bridge.baseline || bridge.baselineLoading)) {
                throw new Error('Wait for baseline statistics before starting a search.');
            }
            const prepared = prepareOptimizerSearch(draft, objective, ranges, options, index);
            if (!prepared.isSearch) return runSingle();
            const identity = JSON.stringify([prepared.draft, prepared.settings, bridge.baseline.source_sha256]);
            if (commandIdentity.current?.identity !== identity) commandIdentity.current = { identity, id: createOptimizerOperationId() };
            return await model.start({ ...prepared.draft, runOperationId: commandIdentity.current.id }, prepared.settings);
        } catch (err) { setError(err.message); return null; }
    }
    return { model, selection, ranges, isSearch, dimensionCount: dimensions.length, modeEditor, rangeEditor, run, options, setOptions,
        active, busy: model.busy || active, error: error ?? model.error,
        clearError: () => { setError(null); model.clearError(); },
        resetRanges: () => { setRanges({}); setError(null); },
    };
}
